// src/components/ClientMinorCreateFromPickerModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Row, Col, Form, Table, InputGroup, Pagination, Badge } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import clientService from '../services/clientService';

const normalize = (s) =>
  (s ?? '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

export default function ClientMinorCreateFromPickerModal({
  show,
  onHide,
  mode = 'create',                // 'create' | 'edit'
  defaultParent = null,           // objet client (facultatif)
  initialMinor = null,            // { id, parentId, firstName, ... , parent? }
  onSave,                         // async ({ parentId, fields, id? })
}) {
  // 🔎 état de recherche/pagination côté serveur
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(false);

  // 👪 sélection de parent
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [selectedParentLabel, setSelectedParentLabel] = useState(''); // affichage clair

  // ⚠️ gestion erreurs locales
  const [error, setError] = useState('');

  // Form mineur
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
    mode: 'onTouched',
    defaultValues: {
      firstName: '',
      lastName: '',
      birthDate: '',
      relation: '',
      email: '',
      phone: '',
      notes: '',
      active: true,
    },
  });

  // Charger une page de parents
  const loadParents = async ({ query = q, p = page, ps = pageSize } = {}) => {
    setLoading(true);
    try {
      const data = await clientService.searchPaged({ q: query, page: p, pageSize: ps });
      setParents(data.rows || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (e) {
      setParents([]);
      setTotal(0);
      setTotalPages(1);
      setError('Chargement des parents impossible.');
    } finally {
      setLoading(false);
    }
  };

  // (Re)charger quand la modale s’ouvre / q / page / pageSize changent
  useEffect(() => {
    if (!show) return;
    loadParents();
  }, [show, q, page, pageSize]); // eslint-disable-line

  // Pré-sélection (create vs edit) + reset form
  useEffect(() => {
    if (!show) return;

    // Pré-sélection du parent
    if (mode === 'edit' && initialMinor?.parentId) {
      setSelectedParentId(initialMinor.parentId);
      const lbl = initialMinor.parent
        ? `${initialMinor.parent.firstName} ${initialMinor.parent.lastName} (${initialMinor.parent.nifCin || '—'})`
        : `#${initialMinor.parentId}`;
      setSelectedParentLabel(lbl);
    } else if (defaultParent?.id) {
      setSelectedParentId(defaultParent.id);
      const lbl = `${defaultParent.firstName} ${defaultParent.lastName} (${defaultParent.nifCin || '—'})`;
      setSelectedParentLabel(lbl);
    } else {
      setSelectedParentId(null);
      setSelectedParentLabel('');
    }

    // Valeurs du formulaire
    if (mode === 'edit' && initialMinor) {
      reset({
        firstName: initialMinor.firstName || '',
        lastName: initialMinor.lastName || '',
        birthDate: initialMinor.birthDate || '',
        relation: initialMinor.relation || '',
        email: initialMinor.email || '',
        phone: initialMinor.phone || '',
        notes: initialMinor.notes || '',
        active: initialMinor.active ?? true,
      });
    } else {
      reset({
        firstName: '',
        lastName: '',
        birthDate: '',
        relation: '',
        email: '',
        phone: '',
        notes: '',
        active: true,
      });
    }

    setError('');
    setPage(1); // repartir page 1 quand on ouvre
  }, [show, mode, initialMinor, defaultParent, reset]);

  // Quand on clique un parent dans le tableau, on met à jour l’étiquette
  const selectParent = (p) => {
    setSelectedParentId(p.id);
    setSelectedParentLabel(`${p.firstName} ${p.lastName} (${p.nifCin || '—'})`);
  };

  // Résumé “parent sélectionné”
  const SelectedParentInfo = () => {
    if (!selectedParentId) return null;
    return (
      <div className="mb-2">
        <Badge bg="info" className="me-2">Parent sélectionné</Badge>
        <span className="text-muted">{selectedParentLabel || `#${selectedParentId}`}</span>
      </div>
    );
  };

  const submit = async (values) => {
    if (!selectedParentId) {
      setError('Veuillez sélectionner un parent.');
      return;
    }
    setError('');
    await onSave({ parentId: selectedParentId, fields: values, id: initialMinor?.id });
  };

  // Pagination UI
  const renderPagination = () => (
    <div className="d-flex justify-content-between align-items-center mt-2">
      <div className="text-muted">Résultats : {total} • Page {page}/{totalPages}</div>
      <div className="d-flex align-items-center">
        <Form.Select
          size="sm"
          value={pageSize}
          onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
          className="me-2"
          style={{ width: 90 }}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </Form.Select>
        <Pagination className="mb-0">
          <Pagination.Prev disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} />
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
            .map((p, idx, arr) => {
              const prev = arr[idx - 1];
              const needEllipsis = prev && p - prev > 1;
              return (
                <React.Fragment key={p}>
                  {needEllipsis && <Pagination.Ellipsis disabled />}
                  <Pagination.Item active={p === page} onClick={() => setPage(p)}>
                    {p}
                  </Pagination.Item>
                </React.Fragment>
              );
            })}
          <Pagination.Next disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} />
        </Pagination>
      </div>
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{mode === 'edit' ? 'Modifier un compte mineur' : 'Créer un compte mineur'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit(submit)} noValidate>
        <Modal.Body>
          {error && <div className="alert alert-danger">{error}</div>}

          <Row className="g-3">
            <Col md={12}>
              <h6 className="mb-2">Choisir le parent</h6>

              <InputGroup className="mb-2" style={{ maxWidth: 360 }}>
                <Form.Control
                  placeholder="Rechercher un client…"
                  value={q}
                  onChange={e => { setQ(e.target.value); setPage(1); }}
                />
                <Button variant="outline-secondary" onClick={() => { setQ(''); setPage(1); }}>×</Button>
              </InputGroup>

              <SelectedParentInfo />

              <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
                <Table hover responsive size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}></th>
                      <th>Prénom</th>
                      <th>Nom</th>
                      <th>NIF/CIN</th>
                      <th>Email</th>
                      <th>Téléphone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={6} className="text-center text-muted">Chargement…</td></tr>
                    )}
                    {!loading && parents.map(p => (
                      <tr
                        key={p.id}
                        style={{ cursor: 'pointer', background: selectedParentId === p.id ? 'rgba(0,123,255,.08)' : undefined }}
                        onClick={() => selectParent(p)}
                      >
                        <td>
                          <Form.Check
                            type="radio"
                            name="parentPick"
                            checked={selectedParentId === p.id}
                            onChange={() => selectParent(p)}
                          />
                        </td>
                        <td>{p.firstName}</td>
                        <td>{p.lastName}</td>
                        <td>{p.nifCin}</td>
                        <td>{p.email || '—'}</td>
                        <td>{p.phone || '—'}</td>
                      </tr>
                    ))}
                    {!loading && parents.length === 0 && (
                      <tr><td colSpan={6} className="text-center text-muted">Aucun client.</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {renderPagination()}
            </Col>

            <Col md={12} className="mt-3">
              <h6>Informations du mineur</h6>
            </Col>

            <Col md={6}>
              <Form.Group controlId="m-firstName">
                <Form.Label>Prénom *</Form.Label>
                <Form.Control
                  {...register('firstName', { required: 'Requis', maxLength: { value: 50, message: 'Max 50 caractères' } })}
                />
                {errors.firstName && <Form.Text className="text-danger">{errors.firstName.message}</Form.Text>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="m-lastName">
                <Form.Label>Nom *</Form.Label>
                <Form.Control
                  {...register('lastName', { required: 'Requis', maxLength: { value: 50, message: 'Max 50 caractères' } })}
                />
                {errors.lastName && <Form.Text className="text-danger">{errors.lastName.message}</Form.Text>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="m-birthDate" className="mt-2">
                <Form.Label>Date de naissance</Form.Label>
                <Form.Control type="date" {...register('birthDate')} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="m-relation" className="mt-2">
                <Form.Label>Lien</Form.Label>
                <Form.Control {...register('relation')} placeholder="Fils, Fille…" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="m-email" className="mt-2">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" {...register('email', { pattern: { value: /^\S+@\S+\.\S+$/, message: 'Email invalide' } })} />
                {errors.email && <Form.Text className="text-danger">{errors.email.message}</Form.Text>}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="m-phone" className="mt-2">
                <Form.Label>Téléphone</Form.Label>
                <Form.Control {...register('phone', { pattern: { value: /^\+?[0-9]{8,15}$/, message: '8–15 chiffres' } })} />
                {errors.phone && <Form.Text className="text-danger">{errors.phone.message}</Form.Text>}
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group controlId="m-notes" className="mt-2">
                <Form.Label>Notes</Form.Label>
                <Form.Control as="textarea" rows={2} {...register('notes')} />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Check type="switch" label="Actif" {...register('active')} className="mt-2" />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Annuler</Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {mode === 'edit' ? 'Enregistrer' : 'Créer'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
